import React, { useEffect, useRef, useState } from "react";
import { PageHeader, Surface } from "@/components/app/Primitives";
import { BookOpen, Printer } from "lucide-react";

// The manual body is a static fragment in /public/manual-assets/manual.html with its
// screenshots beside it. It is fetched rather than bundled so a wording fix or
// a fresh screenshot ships without a code change.
const SECTIONS = [
    { id: "start", label: "Before you start" },
    { id: "signin", label: "Signing in" },
    { id: "hr", label: "Part A · HR administrator", children: [
        ["a1", "A1 Dashboard"], ["a2", "A2 Add or find an employee"], ["a3", "A3 Complete an employee record"],
        ["a4", "A4 Import employees"], ["a5", "A5 Organization"], ["a6", "A6 Attendance, shifts, holidays"],
        ["a7", "A7 Approve leave and overtime"], ["a8", "A8 Users and roles"], ["a9", "A9 Tardiness, undertime, AWOL"], ["a10", "A10 Bell and Approvals Inbox"],
    ] },
    { id: "pay", label: "Part B · Payroll", children: [
        ["b0", "B0 Who does what"], ["b1", "B1 Create the cutoff"], ["b2", "B2 Validate and compute"],
        ["b3", "B3 Check the figures"], ["b4", "B4 Submit, approve, lock"], ["b5", "B5 Bank file"],
        ["b6", "B6 Government reports"], ["b7", "B7 Payroll Control"], ["b8", "B8 Employee loans"], ["b9", "B9 Delete or unlock"], ["b10", "B10 Half-day rule"],
        ["b11", "B11 Allowances, hazard pay"], ["b12", "B12 Bonuses, deductions"], ["b13", "B13 Pay a loan early"], ["b14", "B14 Leave without pay"],
    ] },
    { id: "help", label: "Messages and fixes" },
];

export default function ManualPage() {
    const [html, setHtml] = useState(null);
    const [failed, setFailed] = useState(false);
    const [active, setActive] = useState("start");
    const root = useRef(null);

    useEffect(() => {
        fetch("/manual-assets/manual.html", { cache: "no-cache" })
            .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
            .then(setHtml)
            .catch(() => setFailed(true));
    }, []);

    const go = (id) => {
        const el = root.current?.querySelector(`#${id}`);
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); setActive(id); }
    };

    // Highlight the section being read.
    useEffect(() => {
        if (!html || !root.current) return undefined;
        const ids = SECTIONS.flatMap((s) => [s.id, ...(s.children || []).map((c) => c[0])]);
        const els = ids.map((i) => root.current.querySelector(`#${i}`)).filter(Boolean);
        const io = new IntersectionObserver((entries) => {
            const hit = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
            if (hit) setActive(hit.target.id);
        }, { rootMargin: "-10% 0px -75% 0px" });
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [html]);

    // Click a screenshot to open it at full size.
    const onBodyClick = (e) => {
        if (e.target.tagName === "IMG") window.open(e.target.src, "_blank", "noopener");
    };

    return (
        <div data-testid="user-manual-page">
            <PageHeader
                title="User Manual"
                subtitle="Step by step for HR administrators and payroll staff, with screenshots."
                actions={
                    <button type="button" onClick={() => window.print()}
                        className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50 print:hidden">
                        <Printer className="h-4 w-4" /> Print or save as PDF
                    </button>
                }
            />
            <style>{CSS}</style>
            <div className="manual-layout">
                <nav className="manual-toc print:hidden" aria-label="Manual contents">
                    <div className="manual-toc-title"><BookOpen className="h-4 w-4" /> Contents</div>
                    {SECTIONS.map((s) => (
                        <div key={s.id}>
                            <button type="button" className={`manual-toc-item ${active === s.id ? "on" : ""}`} onClick={() => go(s.id)}>{s.label}</button>
                            {(s.children || []).map(([id, label]) => (
                                <button type="button" key={id} className={`manual-toc-sub ${active === id ? "on" : ""}`} onClick={() => go(id)}>{label}</button>
                            ))}
                        </div>
                    ))}
                </nav>
                <Surface className="manual-surface">
                    {failed && <p>The manual could not be loaded. Refresh the page, or tell your administrator.</p>}
                    {!html && !failed && <p>Loading the manual…</p>}
                    {html && (
                        // Static, first-party content shipped with the app; never user input.
                        <div ref={root} className="manual-body" onClick={onBodyClick} dangerouslySetInnerHTML={{ __html: html }} />
                    )}
                </Surface>
            </div>
        </div>
    );
}

const CSS = `
.manual-layout{display:grid;grid-template-columns:230px minmax(0,1fr);gap:20px;align-items:start}
.manual-toc{position:sticky;top:12px;max-height:calc(100vh - 24px);overflow:auto;font-size:13.5px;padding-right:4px}
.manual-toc-title{display:flex;gap:6px;align-items:center;font-weight:600;margin-bottom:8px}
.manual-toc-item,.manual-toc-sub{display:block;width:100%;text-align:left;border:0;background:none;cursor:pointer;border-radius:6px;color:inherit}
.manual-toc-item{padding:6px 8px;font-weight:600}.manual-toc-sub{padding:3px 8px 3px 20px;opacity:.8}
.manual-toc-item:hover,.manual-toc-sub:hover{background:rgba(15,76,129,.08)}
.manual-toc .on{background:rgba(15,76,129,.12);color:#0f4c81;opacity:1}
.manual-body{max-width:820px;line-height:1.6}
.manual-body h2{font-size:22px;font-weight:700;margin:40px 0 14px;padding-top:10px;border-top:2px solid rgba(0,0,0,.08);scroll-margin-top:12px}
.manual-body h2:first-child{margin-top:0;border-top:0}
.manual-body h3{font-size:17px;font-weight:600;margin:0 0 6px}
.manual-body h3.anchor{height:0;margin:0;overflow:hidden;scroll-margin-top:12px}
.manual-body .step{display:grid;grid-template-columns:44px minmax(0,1fr);gap:12px;margin:22px 0}
.manual-body .num{font:600 12px ui-monospace,monospace;color:#0f4c81;background:#e6eff8;border-radius:8px;height:32px;min-width:32px;display:flex;align-items:center;justify-content:center;align-self:start}
.manual-body .who{margin-left:8px;font:500 11px ui-monospace,monospace;background:#e3efe9;padding:2px 8px;border-radius:99px;white-space:nowrap;vertical-align:middle}
.manual-body p{margin:6px 0}
.manual-body figure{margin:12px 0 4px}
.manual-body figure img{width:100%;height:auto;border:1px solid rgba(0,0,0,.12);border-radius:8px;cursor:zoom-in;display:block}
.manual-body figcaption{font-size:12.5px;opacity:.7;margin-top:5px}
.manual-body .mbtn{display:inline-block;font:600 12.5px/1.4 system-ui,sans-serif;background:#e6eff8;color:#0f4c81;border:1px solid rgba(0,0,0,.1);border-radius:6px;padding:0 7px;white-space:nowrap}
.manual-body code{font:500 13px ui-monospace,monospace;background:#e6eff8;padding:1px 5px;border-radius:4px}
.manual-body table{width:100%;border-collapse:collapse;margin:10px 0;font-size:14px}
.manual-body th,.manual-body td{text-align:left;padding:8px 10px;border-bottom:1px solid rgba(0,0,0,.1);vertical-align:top}
.manual-body th{font-size:11px;letter-spacing:.05em;text-transform:uppercase;opacity:.65}
.manual-body .note,.manual-body .tip{border-radius:8px;padding:10px 14px;margin:12px 0;font-size:14px}
.manual-body .note{background:#e6eff8;border-left:4px solid #0f4c81}
.manual-body .tip{background:#fbf1dc;border-left:4px solid #8a5a00}
.manual-body .flow{display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin:8px 0 12px}
.manual-body .flow span{border:1px solid rgba(0,0,0,.15);border-radius:99px;padding:2px 11px;font:500 12px ui-monospace,monospace}
.manual-body .flow i{font-style:normal;opacity:.6}
.manual-body .foot{opacity:.7;margin-top:20px}
@media (max-width:900px){.manual-layout{grid-template-columns:1fr}.manual-toc{position:static;max-height:none}.manual-body .step{grid-template-columns:1fr}}
@media print{.manual-layout{display:block}.manual-body .step{break-inside:avoid}.manual-body h2{break-before:page}}
`;
